import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, CreditCard, ExternalLink } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { paths, SalesSettingsSchema } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call, CallError } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useCardPayments } from "../../hooks/useCardPayments";

// Setup → Card payments (owner only): connect the company's own Stripe
// account (Stripe Connect). Stripe runs the setup pages and the verification; the money goes
// to the company's bank. Card buttons on jobs and invoices turn on once
// Stripe says charges are enabled.

const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export function PaymentsPage() {
  const { tenantId } = useAuth();
  const { payments } = useCardPayments(tenantId);
  const [busy, setBusy] = useState<"start" | "check" | null>(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const returned = useRef(false);
  const param = new URLSearchParams(window.location.search).get("stripe");

  async function start() {
    if (!tenantId) return;
    setBusy("start");
    setError("");
    try {
      const res = await call<{ url: string }>("startCardPayments", { tenantId }, { timeoutMs: 60_000 });
      window.location.href = res.url;
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
      setBusy(null);
    }
  }
  async function check(quiet = false) {
    if (!tenantId) return;
    setBusy("check");
    setError("");
    try {
      await call("refreshCardPayments", { tenantId }, { mutation: false });
      if (!quiet) setNote("Checked with Stripe just now.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  // Back from Stripe's setup pages: ask Stripe where things stand.
  useEffect(() => {
    if (param === "return" && tenantId && !returned.current) {
      returned.current = true;
      void check(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param, tenantId]);

  if (!payments) return <p className="text-sm text-steel">Loading…</p>;
  const s = payments.status;
  const started = !!payments.stripeAccountId;

  const banner =
    s === "active"
      ? { icon: CheckCircle2, tone: "border-success/40 bg-success/5", title: "Ready: customers can pay by card", text: "From a job's Payment card, a texted pay link, or a copied link on an invoice. The money goes to your Stripe account and on to your bank." }
      : s === "needs_info"
        ? { icon: AlertTriangle, tone: "border-brandorange/50 bg-brandorange/5", title: "Taking cards, but Stripe needs a few more details", text: payments.requirementsDeadline ? `Send them by ${fmtDay(payments.requirementsDeadline)} so payments and payouts keep working.` : "Send them soon so payments and payouts keep working." }
        : s === "restricted"
          ? { icon: AlertTriangle, tone: "border-danger/40 bg-danger/5", title: "Stripe has paused card payments", text: payments.disabledReason ? `Stripe's reason: ${payments.disabledReason.replace(/[._]/g, " ")}.` : "Stripe needs something fixed before cards work again." }
          : s === "pending"
            ? { icon: Clock, tone: "border-steel/30 bg-cream", title: "Stripe is reviewing your details", text: "Usually a few minutes, sometimes a day or two. This page updates by itself when Stripe is done." }
            : s === "onboarding"
              ? { icon: Clock, tone: "border-brandorange/50 bg-brandorange/5", title: "Setup isn't finished", text: "Pick up where you left off: Stripe saved what you entered." }
              : null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-bold">Card payments</h1>
      {param === "refresh" ? (
        <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">That Stripe setup link expired (they only last a few minutes). Continue below to get a fresh one.</p>
      ) : null}
      {error ? <p className="rounded-lg bg-danger/10 px-3 py-1.5 text-xs text-danger">{error}</p> : null}
      {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}

      {banner ? (
        <Card className={cn("flex gap-3", banner.tone)}>
          <banner.icon size={20} className="mt-0.5 shrink-0 text-ink" />
          <div className="min-w-0 space-y-1">
            <div className="font-semibold text-ink">{banner.title}</div>
            <p className="text-sm text-ink/80">{banner.text}</p>
            {payments.requirementsDue.length && s !== "pending" ? (
              <ul className="list-inside list-disc text-sm text-ink">
                {payments.requirementsDue.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {s === "onboarding" || s === "restricted" || s === "needs_info" ? (
                <Button busy={busy === "start"} onClick={() => void start()}>
                  {s === "onboarding" ? "Finish setup with Stripe" : "Update in Stripe"}
                </Button>
              ) : null}
              <Button variant="ghost" className="px-3 py-1.5 text-xs" busy={busy === "check"} onClick={() => void check()}>
                Check status
              </Button>
              {s === "active" || s === "needs_info" ? (
                <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brandorange-dark underline">
                  Your Stripe Dashboard (refunds, payouts, disputes) <ExternalLink size={12} />
                </a>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {!started ? (
        <Card className="space-y-3">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-brandorange" />
            <CardTitle className="mb-0">Take card payments with Stripe</CardTitle>
          </div>
          <p className="text-sm text-ink">
            Customers pay invoices by card on your tech's phone, from a texted link, or online. The money goes into your own Stripe
            account and out to your bank. Stripe's standard processing fee comes out of each payment; you see everything in your own
            Stripe Dashboard.
          </p>
          <ol className="list-inside list-decimal space-y-1 text-sm text-ink">
            <li>Click below. Stripe opens its secure setup pages.</li>
            <li>Enter your business details, EIN, the owner's ID, and the bank account for payouts. Already use Stripe? Sign in instead.</li>
            <li>Come back here. Card buttons turn on as soon as Stripe approves, usually within minutes.</li>
          </ol>
          <Button busy={busy === "start"} onClick={() => void start()}>
            Set up card payments with Stripe
          </Button>
          <p className="text-[11px] text-steel">Clevis never sees card numbers or your bank details; Stripe holds them.</p>
        </Card>
      ) : (
        <Card className="space-y-1 text-sm">
          <CardTitle>Your Stripe account</CardTitle>
          <Row label="Business" value={payments.businessName || "—"} />
          <Row label="Card payments" value={payments.chargesEnabled ? "On" : "Off"} good={payments.chargesEnabled} />
          <Row label="Payouts to your bank" value={payments.payoutsEnabled ? "On" : "Off"} good={payments.payoutsEnabled} />
          <Row label="Stripe account" value={payments.stripeAccountId ?? ""} mono />
          {!payments.livemode ? <Row label="Mode" value="Test: use card 4242 4242 4242 4242, any future date, any CVC" /> : null}
          {payments.checkedAt ? <p className="pt-1 text-[11px] text-steel">Last checked {new Date(payments.checkedAt).toLocaleString()}</p> : null}
        </Card>
      )}

      {tenantId ? <CardFeeCard tenantId={tenantId} /> : null}
    </div>
  );
}

/** The company's card processing fee: its own line at checkout, disclosed on documents. */
function CardFeeCard({ tenantId }: { tenantId: string }) {
  const [pct, setPct] = useState("");
  const [saved, setSaved] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantSalesSettings(tenantId)), (snap) => {
        const bps = SalesSettingsSchema.parse(snap.data() ?? {}).cardFeeBps;
        setSaved(bps);
        setPct(bps ? String(bps / 100) : "");
      }),
    [tenantId],
  );
  const bps = Math.max(0, Math.min(400, Math.round(parseFloat(pct || "0") * 100) || 0));
  async function save() {
    setBusy(true);
    setNote("");
    try {
      await call("setSalesSettings", { tenantId, cardFeeBps: bps });
      setNote(bps ? `Saved. Card payments add ${bps / 100}%.` : "Saved. No card fee.");
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="space-y-2">
      <CardTitle className="mb-0">Card processing fee</CardTitle>
      <p className="text-sm text-ink">
        Pass some or all of the card cost to customers who choose to pay by card. It's added as its own line when they pay online or from a texted link, so they see it before paying, and
        every estimate and invoice says so in the footer. Cash, check and bank payments aren't affected.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Label className="mb-0">Fee</Label>
        <Input type="number" min={0} max={4} step={0.25} className="w-24" value={pct} placeholder="0" onChange={(e) => setPct(e.target.value)} />
        <span className="text-sm text-steel">% (0 = none; card networks cap surcharges at 3–4%)</span>
        <Button type="button" className="px-3 py-1.5 text-xs" busy={busy} disabled={bps === saved} onClick={() => void save()}>
          Save
        </Button>
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
      <p className="text-[11px] text-steel">Surcharging rules vary by state and card network (some states ban it, most require disclosure). Check yours before turning it on.</p>
    </Card>
  );
}

function Row({ label, value, good, mono }: { label: string; value: string; good?: boolean; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-steel/10 py-1 last:border-0">
      <span className="text-steel">{label}</span>
      <span className={cn("text-right", mono && "font-mono text-xs", good === true && "text-success", good === false && "text-danger", good === undefined && "text-ink")}>{value}</span>
    </div>
  );
}
